import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchArea, updateArea, createArea, deleteArea } from "Redux/Admin/HabitManager/Areas/action";

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IArea } from 'Redux/Admin/HabitManager/Areas/type';

const Area = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchAreas);

  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchArea(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createArea(values, handleCreate));
    }
    else if (id) {
      dispatch(updateArea(values, id));
    }
  };

  const handleCreate = (project: IArea) => {
    if (project && project.id) {
      navigate(`${ADMIN_PATH}/habit-manager/areas/${project.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/habit-manager/areas`, { replace: true });
    return null;
  }

  const area = isNew ? initialValues : entities[id];

  if (!area) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Area',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the area?',
    content: <span>Are you sure you want to delete the area <b>{area.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteArea(id, () => {
          navigate(`${ADMIN_PATH}/habit-manager/areas`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/habit-manager/areas`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } Area`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Area
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={area}
                errorData={error}
                isNew={isNew}
                isFormData={true}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default Area;
